/**
 * Générateur — Il résout des problèmes en utilisant la proportionnalité en
 * géométrie (agrandissements et réductions).
 * Attendu ID : 237 | Niveau : 3ème | Domaine : Transformations (d19, O7)
 *
 * Source doc prof (GRANDEURS ET MESURES 3e, séance D4S5). Plan auto-validé
 * 2026-07-17 (TODO §6) :
 *   « reconnaitre » — agrandissement, réduction ou DÉFORMATION ? (les angles !)
 *   « airek2 »      — aire de l'image par une réduction (× k²)
 *   « depuisaires » — retrouver k depuis les aires (racine)
 *   « volumek3 »    — sphères R = m·r (V = m³·v) et maquette au 1/n
 *   « tronc »       — pyramide coupée à h/n : V' = V/n³, tronc = (n³−1)V/n³
 *   « conecoupe »   — cône coupé : k puis V × k³ (valeurs exactes)
 */

import { randomChoice, randInt, tex, makeQcm, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 237;
const L_RECONNAITRE = 'Agrandissement, réduction ou déformation ?';
const L_AIREK2 = 'L\'aire d\'une image réduite';
const L_DEPUISAIRES = 'Retrouver le rapport depuis les aires';
const L_VOLUMEK3 = 'Le volume est multiplié par k³';
const L_TRONC = 'La pyramide coupée : petit volume et tronc';
const L_CONECOUPE = 'Le cône coupé par un plan parallèle';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frNb = (x) => String(Math.round(x * 1000) / 1000).replace('.', ',');

// ═════════ « reconnaitre » ═════════

const genReconnaitreQcm = () => {
    const cas = randomChoice(['agrandissement', 'reduction', 'deformation']);
    const a = randInt(3, 8); const b = a + randInt(1, 4);
    const angle = randomChoice([35, 48, 62, 75]);
    let desc; let correct;
    // « Ni l'un ni l'autre : les angles ont changé » portait sa propre
    // justification, alors que les deux autres choix n'en portaient pas.
    const chA = 'Un agrandissement'; const chR = 'Une réduction'; const chD = 'Ni l\'un ni l\'autre';
    if (cas === 'agrandissement') {
        const k = randomChoice([2, 3]);
        desc = `côtés ${a} cm et ${b} cm → ${a * k} cm et ${b * k} cm, angles inchangés à ${angle}°`;
        correct = chA;
    } else if (cas === 'reduction') {
        desc = `côtés ${2 * a} cm et ${2 * b} cm → ${a} cm et ${b} cm, angles inchangés à ${angle}°`;
        correct = chR;
    } else {
        const k = randomChoice([2, 3]);
        desc = `côtés ${a} cm et ${b} cm → ${a * k} cm et ${b * k} cm, MAIS l'angle passe de ${angle}° à ${Math.min(170, angle * 2)}°`;
        correct = chD;
    }
    const payload = makeQcm([chA, chR, chD], correct,
        `<p>On compare deux parallélogrammes : ${desc}.</p>`
        + '<p><b>La seconde figure est-elle un agrandissement ou une réduction de la première ?</b></p>',
        opts(L_RECONNAITRE));
    payload._meta.params = { cas };
    return payload;
};

// ═════════ « airek2 » ═════════

const genAireK2Trous = () => {
    const aire = randomChoice([25, 50, 51, 75, 100, 120]);
    const k10 = randomChoice([2, 4, 5, 6, 8]);             // réduction k = k10/10 < 1
    const a2 = frNb((aire * k10 * k10) / 100);
    const objet = randomChoice(['de la base d\'un cylindre', 'd\'un logo', 'd\'une affiche', 'd\'un patron']);
    const phrase = `$\\text{Nouvelle aire : } {{${a2}}}\\ \\text{cm}^{2}$`;
    const payload = makeTexteTrou(phrase,
        `<p>L'aire ${objet} vaut ${aire} cm². On applique une réduction de rapport ${frNb(k10 / 10)}.</p>`
        + '<p><b>Calcule l\'aire de l\'image.</b></p>',
        { ...opts(L_AIREK2), mode: 'saisie' });
    payload._meta.params = { aire, k10 };
    return payload;
};

// ═════════ « depuisaires » ═════════

const genDepuisAiresTrous = () => {
    // 85 RETIRÉ (fix rapport 3e, 2026-07-28) : k = 0,85 donne k² = 0,7225, soit
    // 4 décimales — frNb arrondit à 3, le trou attendait donc « 0,723 », valeur
    // FAUSSE qui refusait en prime la réponse exacte. Seule valeur du pool dans
    // ce cas (≈ 1 tirage sur 7) ; les autres donnent k² à 2 décimales.
    const k100 = randomChoice([40, 50, 60, 70, 80, 90]);   // k = k100/100
    const a1 = randomChoice([100, 200, 400, 800]);         // a1·k100²/10000 exact (≤ 2 décimales)
    const a2 = frNb((a1 * k100 * k100) / 10000);
    const k2 = frNb((k100 * k100) / 10000);
    const k = frNb(k100 / 100);
    const phrase = `$k^{2} = {{${k2}}} \\quad\\text{donc}\\quad k = {{${k}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une figure de ${a1} cm² devient ${a2} cm² après une réduction de rapport k.</p>`
        + `<p><b>Calcule ${tex('k^{2}')}, puis k.</b></p>`,
        { ...opts(L_DEPUISAIRES), mode: 'saisie' });
    payload._meta.params = { k100, a1 };
    return payload;
};

// ═════════ « volumek3 » ═════════

const genVolumeK3Trous = () => {
    if (randInt(0, 1)) {
        // Sphères : R = m·r → V = m³ · v.
        const m = randInt(2, 5);
        const phrase = `$V = {{${m * m * m}}} \\times v$`;
        const payload = makeTexteTrou(phrase,
            `<p>Deux sphères ont pour rayons r et ${tex(`R = ${m}r`)}. On note v et V leurs volumes.</p>`
            + '<p><b>Exprime V en fonction de v.</b></p>',
            { ...opts(L_VOLUMEK3), mode: 'saisie' });
        payload._meta.params = { famille: 'spheres', m };
        return payload;
    }
    // Maquette au 1/n : V' = V/n³, V multiple de n³ → entier.
    const n = randomChoice([10, 20, 50, 100]);
    const n3 = n * n * n;
    const vMaquetteCm3 = randInt(20, 90);                  // volume de la maquette en cm³… construit à l'envers
    const vReelCm3 = vMaquetteCm3 * n3;                    // volume réel en cm³
    const vReelM3 = vReelCm3 / 1000000;
    const grp = (x) => String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');                    // en m³
    const phrase = `$\\text{Volume de la maquette : } {{${vMaquetteCm3}}}\\ \\text{cm}^{3}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un monument a un volume de ${frNb(vReelM3)} m³, soit ${grp(vReelCm3)} cm³. `
        + `On en construit une maquette à l'échelle ${tex(`\\dfrac{1}{${n}}`)}.</p>`
        + '<p><b>Calcule le volume de la maquette en cm³.</b></p>',
        { ...opts(L_VOLUMEK3), mode: 'saisie' });
    payload._meta.params = { famille: 'maquette', n, vMaquetteCm3 };
    return payload;
};

// ═════════ « tronc » — coupe à h/n ═════════

const genTroncTrous = () => {
    const n = randomChoice([2, 3, 4, 5]);
    const n3 = n * n * n;
    const solide = randomChoice(['une pyramide', 'un cône']);
    const petit = solide === 'un cône' ? 'le petit cône' : 'la petite pyramide';
    const h = randomChoice([9, 12, 15, 18, 21, 24]);       // hauteur d'HABILLAGE (le résultat n'en dépend pas)
    const phrase = `$V' = \\dfrac{V}{{{${n3}}}} \\qquad \\text{tronc : } \\dfrac{{{${n3 - 1}}}V}{{{${n3}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On coupe ${solide} de volume V et de hauteur ${h} cm par un plan parallèle à la base, à `
        + `${h % n === 0 ? `${h / n} cm du sommet, soit à ${tex(`\\dfrac{1}{${n}}`)} de la hauteur`
            : `${tex(`\\dfrac{1}{${n}}`)} de la hauteur en partant du sommet`} : `
        + `${petit} est une réduction de rapport ${tex(`\\dfrac{1}{${n}}`)}.</p>`
        + `<p><b>Exprime le volume V' de ${petit}, puis celui du tronc ${tex("V - V'")}.</b></p>`,
        { ...opts(L_TRONC), mode: 'saisie' });
    payload._meta.params = { n, n3, solide, h };
    return payload;
};

// ═════════ « conecoupe » ═════════

const genConeCoupeTrous = () => {
    let SO; let SOp; let k10; let V; let vPetit;
    for (let essai = 0; essai < 60; essai++) {
        SO = randomChoice([10, 20]);
        k10 = randomChoice([4, 5, 6, 7, 8]);               // k = k10/10
        SOp = (SO * k10) / 10;
        V = randomChoice([1000, 2000, 3000, 5000]);
        vPetit = (V * k10 * k10 * k10) / 1000;
        if (Number.isInteger(SOp) && Number.isInteger(vPetit)) break;
        vPetit = null;
    }
    if (vPetit === null) { SO = 10; SOp = 7; k10 = 7; V = 1000; vPetit = 343; }
    const phrase = `$k = {{${frNb(k10 / 10)}}} \\qquad \\text{volume du petit cône : } {{${vPetit}}}\\ \\text{cm}^{3}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un cône de sommet S a une hauteur ${tex(`SO = ${SO}`)} cm et un volume de ${V} cm³. `
        + `On le coupe par un plan parallèle à la base, à ${tex(`SO' = ${SOp}`)} cm du sommet : `
        + 'le petit cône est une réduction du grand.</p>'
        + `<p><b>Donne le coefficient de réduction ${tex("k = \\dfrac{SO'}{SO}")}, puis le volume `
        + 'du petit cône.</b></p>',
        { ...opts(L_CONECOUPE), mode: 'saisie' });
    payload._meta.params = { SO, SOp, k10, V, vPetit };
    return payload;
};

const GENERATORS = {
    reconnaitre_qcm_unique: genReconnaitreQcm,
    airek2_texte_trou_saisie: genAireK2Trous,
    depuisaires_texte_trou_saisie: genDepuisAiresTrous,
    volumek3_texte_trou_saisie: genVolumeK3Trous,
    tronc_texte_trou_saisie: genTroncTrous,
    conecoupe_texte_trou_saisie: genConeCoupeTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'reconnaitre', label: L_RECONNAITRE, formatsList: buildFormats('reconnaitre') },
    { id: 'airek2', label: L_AIREK2, formatsList: buildFormats('airek2') },
    { id: 'depuisaires', label: L_DEPUISAIRES, formatsList: buildFormats('depuisaires') },
    { id: 'volumek3', label: L_VOLUMEK3, formatsList: buildFormats('volumek3') },
    { id: 'tronc', label: L_TRONC, formatsList: buildFormats('tronc') },
    { id: 'conecoupe', label: L_CONECOUPE, formatsList: buildFormats('conecoupe') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
